import { FC, useMemo } from "react";

import { Box } from "@mui/material";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RouterLink from "../../../common/components/RouterLink";
import DeviceModelChipDropdown from "../../../common/components/chip/DeviceModelChipDropdown";
import { ServiceTypeEnum } from "../../model-manager/variables/modelManager";
import { IDeviceTableRow } from "../hooks/useDeviceTableRows";
import AddModelButton from "./AddModelStyledButton";

interface IConnectedModelsColumnProps {
  device: IDeviceTableRow;
}

const DeviceConnectedModelsColumn: FC<IConnectedModelsColumnProps> = ({
  device,
}) => {
  // Services are now pre-fetched and passed via device.models from useDeviceTableRows
  const services = device.models ?? [];

  const showAddModelButton =
    device.modelsToRun?.length > (services?.length ?? 0);

  const memoizedRunningLeakModel = useMemo(() => {
    const runningLeakModel = services?.find(
      m =>
        m.isRunning &&
        (m.serviceName === ServiceTypeEnum.LEAK ||
          m.serviceName === ServiceTypeEnum.GAS_LEAK)
    );

    return runningLeakModel;
  }, [services]);

  const memoizedRunningLiquidLeakModel = useMemo(() => {
    const runningLiquidLeakModel = services?.find(
      m => m.isRunning && m.serviceName === ServiceTypeEnum.LIQUID_LEAK
    );

    return runningLiquidLeakModel;
  }, [services]);

  const memoizedRunningLeakModels = useMemo(() => {
    let runningLeakModels: string[] = [];

    if (memoizedRunningLeakModel) {
      runningLeakModels = [memoizedRunningLeakModel.serviceName ?? ""];
    }

    if (memoizedRunningLiquidLeakModel) {
      runningLeakModels = [memoizedRunningLiquidLeakModel.serviceName ?? ""];
    }

    if (memoizedRunningLeakModel && memoizedRunningLiquidLeakModel) {
      runningLeakModels = [
        memoizedRunningLeakModel.serviceName ?? "",
        memoizedRunningLiquidLeakModel.serviceName ?? "",
      ];
    }

    return runningLeakModels;
  }, [memoizedRunningLeakModel, memoizedRunningLiquidLeakModel]);

  return (
    <>
      {services?.map((model): JSX.Element => {
        return (
          <DeviceModelChipDropdown
            key={model.serviceId}
            data={device}
            model={model}
            runningLeakModels={memoizedRunningLeakModels}
          />
        );
      })}

      <Box
        sx={{
          visibility: showAddModelButton ? "visible" : "hidden",
        }}
      >
        {showAddModelButton && (
          <RouterLink
            to={"/device/" + encodeURIComponent(device.rowId)}
            state={{ deviceDatum: device }}
          >
            <AddModelButton
              className="addModelButton"
              disableRipple
              startIcon={<AddRoundedIcon />}
            />
          </RouterLink>
        )}
      </Box>
    </>
  );
};

export default DeviceConnectedModelsColumn;
